import type { SectionRow } from '@/types'

import { Heart } from 'lucide-react'
import { cn } from '@/lib/utils'

import Reveal from '@/components/landing/reveal'

interface SectionHeadingProps {
    section?: SectionRow
    tone?: 'light' | 'dark'
    level?: 'h2' | 'h3'
    size?: 'lg' | 'md'
    className?: string
}

/** Kicker, title and subtitle of a section, all editable in place. */
export default function SectionHeading({
    section,
    tone = 'light',
    level = 'h2',
    size = 'lg',
    className
}: SectionHeadingProps) {
    if (!section) return null

    const Title = level
    const dark = tone === 'dark'

    return (
        <Reveal className={cn('mx-auto max-w-2xl text-center', className)}>
            {section.kicker && (
                <p className={cn(
                    'mb-4 inline-flex items-center gap-3 text-xs font-bold tracking-[0.28em] uppercase',
                    dark ? 'text-[#ff9ab4]' : 'text-petal-deep'
                )}>
                    <Heart aria-hidden className='size-3.5 fill-current' />

                    <span data-mdlfy-cms={`sections:${section.id}:kicker`}>
                        {section.kicker}
                    </span>

                    <Heart aria-hidden className='size-3.5 fill-current' />
                </p>
            )}

            {section.title && (
                <Title
                    data-mdlfy-cms={`sections:${section.id}:title`}
                    className={cn(
                        'font-heading font-semibold tracking-tight text-balance',
                        size === 'lg' ? 'text-4xl leading-[1.08] sm:text-5xl md:text-6xl' : 'text-3xl leading-tight sm:text-4xl',
                        dark ? 'text-cream' : 'text-wine'
                    )}
                >
                    {section.title}
                </Title>
            )}

            {section.subtitle && (
                <p
                    data-mdlfy-cms={`sections:${section.id}:subtitle`}
                    className={cn(
                        'mx-auto mt-4 max-w-xl text-base leading-relaxed text-pretty sm:text-lg',
                        dark ? 'text-cream/75' : 'text-ink/70'
                    )}
                >
                    {section.subtitle}
                </p>
            )}
        </Reveal>
    )
}
